import { CSAT_RATING_LABELS, halloCommaSalutation, renderVoiceCopy } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CsatSurveyForm } from '@/products/helpdesk/features/csat/csat-survey-form';
import {
  useCsatActionPreview,
  useSubmitCsatAction,
} from '@/products/helpdesk/features/csat/use-csat';
import { ApiRequestError } from '@/lib/api';
import { useVoice } from '@/platform/voice/use-voice';

export function CsatActionPage() {
  const voice = useVoice();
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const preview = useCsatActionPreview(token);
  const submit = useSubmitCsatAction(token ?? '');
  const [doneRating, setDoneRating] = useState<number | null>(null);

  const preset = useMemo(() => {
    const raw = Number(params.get('wertung'));
    return raw >= 1 && raw <= 5 ? raw : null;
  }, [params]);

  async function onSubmit(input: { rating: number; comment?: string }) {
    try {
      await submit.mutateAsync(input);
      setDoneRating(input.rating);
      toast.success(renderVoiceCopy('helpdesk.csat.thanks', voice));
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Bewertung konnte nicht gespeichert werden.',
      );
    }
  }

  if (!token) {
    return <p className="text-muted-foreground">Ungültiger Link.</p>;
  }

  if (preview.isPending) {
    return (
      <p className="flex items-center gap-2 text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Umfrage wird geladen …
      </p>
    );
  }

  const data = preview.data;
  if (!data?.ticketNumber) {
    return (
      <section className="mx-auto max-w-lg space-y-3">
        <h1 className="text-2xl font-bold">Umfrage-Link ungültig</h1>
        <p className="text-muted-foreground">Der Link ist ungültig oder unbekannt.</p>
      </section>
    );
  }

  if (doneRating != null || data.alreadyResponded) {
    const rating = doneRating ?? data.rating;
    return (
      <section className="mx-auto max-w-lg space-y-4">
        <h1 className="text-2xl font-bold">Vielen Dank</h1>
        <p className="text-muted-foreground">
          {rating != null
            ? renderVoiceCopy('helpdesk.csat.your-rating', voice, {
                number: String(data.ticketNumber),
                score: `${rating} ${rating === 1 ? 'Stern' : 'Sterne'}${CSAT_RATING_LABELS[rating] ? ` · ${CSAT_RATING_LABELS[rating]}` : ''}`,
              })
            : `Ticket #${data.ticketNumber} wurde bereits bewertet.`}
        </p>
        <Button asChild>
          <Link to={`/portal/tickets/${data.ticketNumber}`}>Ticket öffnen</Link>
        </Button>
      </section>
    );
  }

  const blocked = !data.tokenValid;
  const reason = data.cancelled
    ? 'Diese Umfrage gilt nicht mehr, weil das Ticket wieder geöffnet wurde.'
    : 'Dieser Link kann nicht verwendet werden.';

  return (
    <section className="mx-auto max-w-lg space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {renderVoiceCopy('helpdesk.csat.satisfied', voice)}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {data.recipientDisplayName
            ? `${halloCommaSalutation({ displayName: data.recipientDisplayName }, voice)} `
            : ''}
          eine Sterne-Bewertung zu Ticket #{data.ticketNumber} reicht. Der Kommentar ist optional.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>
            #{data.ticketNumber} · {data.ticketSubject}
          </CardTitle>
          <CardDescription>1 = sehr unzufrieden, 5 = sehr zufrieden</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {blocked ? (
            <p className="text-sm">{reason}</p>
          ) : (
            <CsatSurveyForm
              initialRating={preset}
              pending={submit.isPending}
              onSubmit={(input) => void onSubmit(input)}
            />
          )}
          <Button asChild variant="outline" size="sm">
            <Link to={`/portal/tickets/${data.ticketNumber}`}>Im Portal öffnen</Link>
          </Button>
        </CardContent>
      </Card>
    </section>
  );
}
